import { Country, OwnershipRelationshipType } from '@prisma/client';
import { z } from 'zod';

// Limit fractional digits to log10(percentScale) so FE can't submit values the BE would silently round.
const buildFinitePercent = (percentScale: number) => {
  const maxDecimals = Math.max(0, Math.round(Math.log10(percentScale)));
  return z
    .number({
      required_error: '% owned is required.',
      invalid_type_error: '% owned is required.',
    })
    .min(0, "Ownership can't be below 0%.")
    .max(100, "Ownership can't exceed 100%.")
    .refine((v) => Number.isFinite(v), { message: 'Ownership must be a finite number.' })
    .refine(
      (v) => Number.isInteger(Math.round(v * percentScale)) && Math.abs(v * percentScale - Math.round(v * percentScale)) < 1e-9,
      { message: `Ownership supports up to ${maxDecimals} decimal place${maxDecimals === 1 ? '' : 's'}.` },
    );
};


const optionalDateOfBirth = z
  .union([
    z.literal(''),
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be in YYYY-MM-DD format.'),
  ])
  .nullish();

// Pre-flight check: otherSum + newPercent ≤ max(100, otherSum + oldOwnPercent).
// Per-class: each stockClassId (or the null bucket) has its own pool.
// Adds pass oldOwnPercent = 0; Edit passes the row's current % iff the row
// stays in the same class bucket (moves across classes free up the old pool).
export type OwnershipBudget = { otherSumPercent: number; oldOwnPercent: number };

// Resolves the budget for the class the form is currently writing to.
export type GetOwnershipBudget = (stockClassId: string | null) => OwnershipBudget;

const fitsBudget = (b: OwnershipBudget, newPercent: number) =>
  b.otherSumPercent + newPercent <= Math.max(100, b.otherSumPercent + b.oldOwnPercent) + 1e-9;

const budgetMessage = (b: OwnershipBudget) => {
  const headroom = Math.max(0, Math.max(100, b.otherSumPercent + b.oldOwnPercent) - b.otherSumPercent);
  const suffix = b.otherSumPercent > 0 ? ' Reduce another shareholder to free up space.' : '';
  return `Ownership of this share class would exceed 100% — only ${headroom.toFixed(2)}% is available.${suffix}`;
};

export const ZERO_BUDGET: OwnershipBudget = { otherSumPercent: 0, oldOwnPercent: 0 };

// Skips when % is undefined (Edit) or 0 (control-only / reducing to 0).
const budgetSuperRefine =
  (getBudget: GetOwnershipBudget) =>
  (data: { ownershipPercent?: number; stockClassId?: string }, ctx: z.RefinementCtx) => {
    if (data.ownershipPercent === undefined || data.ownershipPercent === 0) return;
    const budget = getBudget(data.stockClassId ?? null);
    if (!fitsBudget(budget, data.ownershipPercent)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: budgetMessage(budget), path: ['ownershipPercent'] });
    }
  };

const hscOrPositivePercent = (data: { ownershipPercent?: number; hasSignificantControl?: boolean }) =>
  data.ownershipPercent === undefined || data.ownershipPercent > 0 || data.hasSignificantControl === true;

const HSC_OR_POSITIVE_ERROR = {
  message: 'Set ownership above 0%, or check "Has significant control".',
  path: ['ownershipPercent'],
};

// A class is where a stake lives, so it's required exactly when there is one.
// A 0% control-only row has nothing to place — the shape `hscOrPositivePercent`
// already allows.
const buildStockClassRequiredForStake =
  (classLabel: string, required = true) =>
  (data: { ownershipPercent?: number; stockClassId?: string }, ctx: z.RefinementCtx) => {
    // An unelected LLC shows the picker empty, so nothing has been asked for; the server
    // places the member in its sole class.
    if (!required) return;
    if (!data.ownershipPercent) return;
    if (data.stockClassId) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${classLabel} is required.`,
      path: ['stockClassId'],
    });
  };

export const buildAddIndividualShareholderSchema = (
  percentScale: number,
  getBudget: GetOwnershipBudget = () => ZERO_BUDGET,
  classLabel = 'Share class',
  classRequired = true,
) =>
  z
    .object({
      firstName: z.string().trim().min(1, 'First name is required.'),
      lastName: z.string().trim().optional(),
      // Optional here: the add-person sheet enforces DOB on staged-new people,
      // and the existing-director path submits only directorId.
      dateOfBirth: optionalDateOfBirth,
      nationality: z.nativeEnum(Country),
      ownershipPercent: buildFinitePercent(percentScale),
      relationshipType: z.nativeEnum(OwnershipRelationshipType),
      hasSignificantControl: z.boolean().optional(),
      stockClassId: z.string().optional(),
    })
    .refine(hscOrPositivePercent, HSC_OR_POSITIVE_ERROR)
    .superRefine(buildStockClassRequiredForStake(classLabel, classRequired))
    .superRefine(budgetSuperRefine(getBudget));

export type AddIndividualShareholderForm = z.infer<ReturnType<typeof buildAddIndividualShareholderSchema>>;

export const buildAddEntityShareholderSchema = (
  percentScale: number,
  getBudget: GetOwnershipBudget = () => ZERO_BUDGET,
  classLabel = 'Share class',
  classRequired = true,
) =>
  z
    .object({
      stakeholderCorporationId: z.number().int().min(1, 'Source entity is required.'),
      ownershipPercent: buildFinitePercent(percentScale),
      relationshipType: z.nativeEnum(OwnershipRelationshipType),
      hasSignificantControl: z.boolean().optional(),
      stockClassId: z.string().optional(),
    })
    .refine(hscOrPositivePercent, {
      message: 'Entity shareholders with 0% ownership must have significant control.',
      path: ['ownershipPercent'],
    })
    .superRefine(buildStockClassRequiredForStake(classLabel, classRequired))
    .superRefine(budgetSuperRefine(getBudget));

export type AddCorporateShareholderForm = z.infer<ReturnType<typeof buildAddEntityShareholderSchema>>;

// Ownership edits never modify person attributes — `nationality` lives on
// the Person surface (Director.personalDetails.countryOfCitizenship).
export const buildEditStakeholderSchema = (
  percentScale: number,
  getBudget: GetOwnershipBudget = () => ZERO_BUDGET,
  classLabel = 'Share class',
  classRequired = true,
) =>
  z
    .object({
      ownershipPercent: buildFinitePercent(percentScale),
      hasSignificantControl: z.boolean().optional(),
      relationshipType: z.nativeEnum(OwnershipRelationshipType).optional(),
      stockClassId: z.string().optional(),
    })
    .refine(hscOrPositivePercent, HSC_OR_POSITIVE_ERROR)
    .superRefine(buildStockClassRequiredForStake(classLabel, classRequired))
    .superRefine(budgetSuperRefine(getBudget));

export type EditStakeholderForm = z.infer<ReturnType<typeof buildEditStakeholderSchema>>;
